import { createFileRoute, Link, Navigate } from '@tanstack/react-router'
import { ListIcon, MapPinIcon, UserIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAdoption } from '@/api/adoption'
import { lineCountKey, locationLabel, locationOf, useOpeningBalances } from '@/api/opening-balances'
import type { OpeningBalance } from '@/api/opening-balances'
import { useWorkspace } from '@/api/workspace'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatMoney } from '@/components/materials/pool-format'
import { VoidedBadge } from '@/components/corrections/voided'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { RecordLink, RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

// Opening balances (UI-SCREENS I-13), Owner only: the Pools (#65) and the WIP parts (#139); Home
// and channels arrive with 05 Inventory.
export const Route = createFileRoute('/w/$slug/settings/opening-balances/')({
  component: OpeningBalancesList,
  staticData: { breadcrumb: 'settings.openingBalances.title' },
})

function OpeningBalancesList() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const workspace = useWorkspace(slug)
  const documents = useOpeningBalances(slug)
  const adoption = useAdoption(slug)

  // Staff has no settings screens; the API refuses them too.
  if (workspace.data?.role !== 'owner') return <Navigate to="/w/$slug" params={{ slug }} replace />

  const adopted = Boolean(adoption.data?.adopted_on)

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-semibold">{t('settings.openingBalances.title')}</h1>
        {adopted ? (
          <Button asChild className="h-11">
            <Link to="/w/$slug/settings/opening-balances/new" params={{ slug }}>
              {t('settings.openingBalances.new')}
            </Link>
          </Button>
        ) : null}
      </div>
      <p className="max-w-2xl text-sm text-muted-foreground">{t('settings.openingBalances.explanation')}</p>

      {adoption.isSuccess && !adopted ? <NeedsAdoptionDate slug={slug} /> : null}

      {documents.isPending ? (
        <LoadingSkeleton />
      ) : documents.isError || !documents.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : documents.data.length === 0 ? (
        <RecordListEmpty>{t('settings.openingBalances.empty')}</RecordListEmpty>
      ) : (
        <RecordList>
          {documents.data.map((document) => (
            <DocumentRow key={document.id} slug={slug} document={document} />
          ))}
        </RecordList>
      )}
    </section>
  )
}

// While the adoption date is empty nothing can be posted (domain-rules Dates & backdating), so
// the screen sends the Owner to A-11 first.
function NeedsAdoptionDate({ slug }: { slug: string }) {
  const { t } = useTranslation()

  return (
    <Card>
      <CardContent className="flex flex-col items-start gap-3 py-4">
        <p role="status" className="text-sm">
          {t('settings.openingBalances.noAdoptionDate')}
        </p>
        <Button asChild variant="outline" className="h-11">
          <Link to="/w/$slug/settings/workspace" params={{ slug }}>
            {t('settings.openingBalances.goToAdoption')}
          </Link>
        </Button>
      </CardContent>
    </Card>
  )
}

function DocumentRow({ slug, document }: { slug: string; document: OpeningBalance }) {
  const { t } = useTranslation()
  const location = locationOf(document)

  return (
    <RecordListItem
      voided={Boolean(document.voided_at)}
      title={
        <RecordLink
          to="/w/$slug/settings/opening-balances/$openingBalanceId"
          params={{ slug, openingBalanceId: String(document.id) }}
        >
          {formatBusinessDate(document.occurred_on)}
        </RecordLink>
      }
      badges={<VoidedBadge record={document} />}
      meta={[
        { icon: MapPinIcon, label: t('settings.openingBalances.columns.location'), value: locationLabel(t, document) },
        {
          icon: ListIcon,
          label: t('settings.openingBalances.columns.lines'),
          value: t(lineCountKey(location), { count: document.lines.length }),
        },
        document.created_by
          ? {
              icon: UserIcon,
              label: t('settings.openingBalances.columns.createdBy'),
              value: document.created_by.name ?? document.created_by.email,
            }
          : null,
      ]}
      // A WIP or Finished Goods document is qty only: no value at all (null, #139, #173).
      figure={document.total_value === null ? undefined : `Rp ${formatMoney(document.total_value)}`}
    />
  )
}
